import React, { useState, useEffect } from "react";
import { 
    Grid, 
    GridColumn, 
    Dropdown,
    Tab, 
    TabPane,
    TableRow,
    TableHeaderCell,
    TableHeader,
    TableFooter,
    TableCell,
    TableBody,
    Table,
    Input,
    Modal,
    Button
} from "semantic-ui-react";
import axios from "axios";

const WEBSITE_SQL_WEBHOOK = "https://api.axc.ae/webhook/b1acf1f9-ffab-4131-9379-7bfb7cea29fe";

const WEBSITE_SELECT_SQL = `
SELECT
    id,
    integration_id,
    name,
    duplicates_filter,
    source_details,
    ad_project,
    marker,
    responsible,
    tags
FROM websites
ORDER BY id DESC;`.trim();

const normalizeSqlRows = (payload) => {
    let parsedPayload = payload;

    if (typeof parsedPayload === 'string') {
        try {
            parsedPayload = JSON.parse(parsedPayload);
        } catch (error) {
            return [];
        }
    }

    const candidates = [
        parsedPayload,
        parsedPayload?.body,
        parsedPayload?.data,
        parsedPayload?.rows,
        parsedPayload?.result,
        parsedPayload?.items,
        parsedPayload?.json,
        parsedPayload?.[0]?.json,
        parsedPayload?.[0]?.body,
        parsedPayload?.[0]?.data
    ];

    const rows = candidates.find(candidate => Array.isArray(candidate));

    return Array.isArray(rows)
        ? rows.map(row => row?.json || row?.body || row)
        : (console.warn('SQL webhook returned no row array:', parsedPayload), []);
}

const sqlString = (value) => {
    if (value === undefined || value === null || value === '') {
        return 'NULL';
    }

    return `'${value.toString().replace(/'/g, "''")}'`;
}

const sqlNumber = (value) => {
    if (value === undefined || value === null || value === '') {
        return 'NULL';
    }

    return Number(value);
}

const sqlJsonb = (value) => {
    const jsonValue = JSON.stringify(Array.isArray(value) ? value : []);

    return `'${jsonValue.replace(/'/g, "''")}'::jsonb`;
}

const buildWebsiteUpdateSql = (fields) => `
UPDATE websites
SET
    name = ${sqlString(fields.name)},
    duplicates_filter = ${sqlString(fields.duplicates_filter)},
    source_details = ${sqlString(fields.source_details)},
    ad_project = ${sqlNumber(fields.Ad_Project)},
    marker = ${sqlString(fields.marker)},
    responsible = ${sqlNumber(fields.responsible)},
    tags = ${sqlJsonb(fields.tags)}
WHERE id = ${sqlNumber(fields.id)}
RETURNING *;`.trim();

const IntegrationTable = ({ refreshKey }) => {
    const [data, setData] = useState([]);
    const [projects, setProjects] = useState([]);
    const [responsible, setResponsible] = useState([]);
    const [tags, setTags] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sortConfig, setSortConfig] = useState({ column: 'id', direction: 'descending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [saveMessage, setSaveMessage] = useState('');

    const fetchData = async () => {
        try {
            const response = await axios.post(WEBSITE_SQL_WEBHOOK, {
                sql: WEBSITE_SELECT_SQL,
                source: "website"
            })
            setData(normalizeSqlRows(response.data));
        } catch (error) {
            console.error('ошибка ', error);
        }
    }

    const fetchTags = async () => {
        try {
            let start = 0;
            let allTags = [];

            while (start !== null) {
                const response = await axios.post(`https://crm.axcap.ae/rest/1/a9czkhedx3mmmn04/lists.element.get?IBLOCK_TYPE_ID=lists&IBLOCK_ID=189&ELEMENT_ORDER%5BID%5D=ASC&start=${start}`, {})
                allTags = allTags.concat(response.data.result || []);
                start = response.data.next ?? null;
            }

            setTags(allTags);
        } catch (error) {
            console.error('ошибка ', error)
        }
    }

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await axios.post('https://crm.axcap.ae/rest/1/a9czkhedx3mmmn04/crm.lead.userfield.list?filter%5BFIELD_NAME%5D=UF_CRM_1568907438', {})
                setProjects(response.data.result?.[0]?.LIST || []);
            } catch (error) {
                console.error('ошибка ', error)
            }
        }

        const fetchResponsible = async () => {
            try {
                const response = await axios.get('https://crm.axcap.ae/local/webhooks/getUserIdName.php?api_key=UPmcMSpk86SLPprv')
                setResponsible(response.data);
            } catch (error) {
                console.error('ошибка ', error)
            }
        }

        fetchProjects();
        fetchResponsible();
        fetchTags();
        fetchData();
    }, [refreshKey]);

    const transformProject = projects.map(project => ({
        key: project.ID,
        text: project.VALUE,
        value: project.ID
    }))

    const transformResponsible = responsible.map(responsibleItem => ({
        key: responsibleItem.ID,
        text: responsibleItem.NAME,
        value: responsibleItem.ID
    }))
    transformResponsible.push({key: 1, text: 'AX CAPITAL', value: '1'})

    const transformTags = tags.map(tag => ({
        key: tag.ID,
        text: tag.NAME,
        value: tag.ID
    }))

    const columns = [
        { key: 'id', label: 'ID' },
        { key: 'integration_id', label: 'Integration ID' },
        { key: 'name', label: 'URL / Name' },
        { key: 'duplicates_filter', label: 'Duplicates Filter' },
        { key: 'source_details', label: 'Source Details' },
        { key: 'project', label: 'Project' },
        { key: 'marker', label: 'Marker' },
        { key: 'responsible', label: 'Responsible' },
        { key: 'tags', label: 'Tags' }
    ]

    const pageSizeOptions = [
        { key: 10, text: '10', value: 10 },
        { key: 25, text: '25', value: 25 },
        { key: 50, text: '50', value: 50 },
        { key: 100, text: '100', value: 100 }
    ]

    const getProjectValue = (projectId) => {
        const project = projects.find(p => p.ID === projectId?.toString())
        return project ? project.VALUE : 'Undefined';
    }

    const getResponsibleValue = (responsibleId) => {
        if (responsibleId?.toString() === '1') {
            return 'AX CAPITAL';
        }

        const responsibleItem = responsible.find(item => item.ID === responsibleId?.toString())
        return responsibleItem ? responsibleItem.NAME : 'INDIVIDUAL';
    }

    const getTagsValue = (tagIds) => {
        if (!Array.isArray(tagIds) || tagIds.length === 0) {
            return '';
        }

        return tagIds
            .map(tagId => tags.find(tag => tag.ID === tagId?.toString())?.NAME)
            .filter(Boolean)
            .join(', ');
    }

    const getColumnValue = (item, column) => {
        switch (column) {
            case 'id':
                return item.id;
            case 'integration_id':
                return item.integration_id || '';
            case 'name':
                return item.name || item.url || '';
            case 'duplicates_filter':
                return item.duplicates_filter || '';
            case 'source_details':
                return item.source_details || '';
            case 'project':
                return getProjectValue(item.ad_project);
            case 'marker':
                return item.marker || '';
            case 'responsible':
                return getResponsibleValue(item.responsible);
            case 'tags':
                return getTagsValue(item.tags);
            default:
                return '';
        }
    }

    const handleSort = (column) => {
        setSortConfig(prevSort => ({
            column,
            direction: prevSort.column === column && prevSort.direction === 'ascending'
                ? 'descending'
                : 'ascending'
        }))
        setCurrentPage(1)
    }

    const handleColumnFilterChange = (column, value) => {
        setColumnFilters(prevFilters => ({
            ...prevFilters,
            [column]: value
        }))
        setCurrentPage(1)
    }

    const openEditModal = (rowData) => {
        setEditedData({
            id: rowData.id,
            integration_id: rowData.integration_id || '',
            name: rowData.name || rowData.url || '',
            duplicates_filter: rowData.duplicates_filter || '',
            source_details: rowData.source_details || '',
            Ad_Project: rowData.ad_project?.toString() || '',
            marker: rowData.marker || '',
            responsible: rowData.responsible?.toString() || '',
            tags: Array.isArray(rowData.tags) ? rowData.tags.map(tag => tag.toString()) : []
        });
        setSaveMessage('');
        setEditModalOpen(true);
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditedData({});
    };

    const updateEditedData = (fieldName, value) => {
        setEditedData(prevData => ({
            ...prevData,
            [fieldName]: value
        }))
    }

    const handleSave = async () => {
        try {
            const sql = buildWebsiteUpdateSql(editedData);
            const response = await axios.post(WEBSITE_SQL_WEBHOOK, {
                fields: editedData,
                sql,
                source: "website"
            });

            setSaveMessage(response.data?.message || 'Saved');
            await fetchData();
            closeEditModal();
        } catch (error) {
            console.error('Ошибка обновления интеграции: ', error);
            setSaveMessage('Save error');
        }
    };

    const filteredData = data.filter(item => {
        const globalSearch = searchTerm.toLowerCase();
        const matchesGlobalSearch = !globalSearch || columns.some(column =>
            getColumnValue(item, column.key).toString().toLowerCase().includes(globalSearch)
        );

        const matchesColumnFilters = columns.every(column => {
            const filterValue = columnFilters[column.key]?.toLowerCase();

            if (!filterValue) {
                return true;
            }

            return getColumnValue(item, column.key).toString().toLowerCase().includes(filterValue);
        });

        return matchesGlobalSearch && matchesColumnFilters;
    });

    const sortedData = [...filteredData].sort((firstItem, secondItem) => {
        const firstValue = getColumnValue(firstItem, sortConfig.column);
        const secondValue = getColumnValue(secondItem, sortConfig.column);

        if (typeof firstValue === 'number' && typeof secondValue === 'number') {
            return sortConfig.direction === 'ascending'
                ? firstValue - secondValue
                : secondValue - firstValue;
        }

        return sortConfig.direction === 'ascending'
            ? firstValue.toString().localeCompare(secondValue.toString(), undefined, { numeric: true })
            : secondValue.toString().localeCompare(firstValue.toString(), undefined, { numeric: true });
    });

    const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));
    const pageStartIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = sortedData.slice(pageStartIndex, pageStartIndex + itemsPerPage);

    const changePage = (nextPage) => {
        setCurrentPage(Math.min(Math.max(nextPage, 1), totalPages))
    }

    return (
        <div>
            <div className='twilio-table-toolbar'>
                <Input
                    icon='search'
                    placeholder='Search all columns...'
                    value={searchTerm}
                    onChange={(event) => {
                        setSearchTerm(event.target.value);
                        setCurrentPage(1);
                    }}
                />
                <div className='twilio-pagination-size'>
                    <span>Rows</span>
                    <Dropdown
                        selection
                        options={pageSizeOptions}
                        value={itemsPerPage}
                        onChange={(e, dropdownData) => {
                            setItemsPerPage(dropdownData.value)
                            setCurrentPage(1)
                        }}
                    />
                </div>
            </div>
            <div className='twilio-table-scroll'>
                <Table celled sortable className='website-integrations-table'>
                    <TableHeader>
                        <TableRow>
                            {columns.map(column => (
                                <TableHeaderCell
                                    key={column.key}
                                    sorted={sortConfig.column === column.key ? sortConfig.direction : null}
                                    onClick={() => handleSort(column.key)}
                                >
                                    {column.label}
                                </TableHeaderCell>
                            ))}
                            <TableHeaderCell>Edit</TableHeaderCell>
                        </TableRow>
                        <TableRow className='twilio-column-filters'>
                            {columns.map(column => (
                                <TableHeaderCell key={column.key}>
                                    <Input
                                        size='mini'
                                        fluid
                                        placeholder='Filter...'
                                        value={columnFilters[column.key] || ''}
                                        onChange={(event) => handleColumnFilterChange(column.key, event.target.value)}
                                    />
                                </TableHeaderCell>
                            ))}
                            <TableHeaderCell />
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {currentItems.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.id}</TableCell>
                                <TableCell>{item.integration_id}</TableCell>
                                <TableCell>{item.name || item.url}</TableCell>
                                <TableCell>{item.duplicates_filter}</TableCell>
                                <TableCell>{item.source_details}</TableCell>
                                <TableCell>{getProjectValue(item.ad_project)}</TableCell>
                                <TableCell>{item.marker}</TableCell>
                                <TableCell>{getResponsibleValue(item.responsible)}</TableCell>
                                <TableCell>{getTagsValue(item.tags)}</TableCell>
                                <TableCell>
                                    <Button onClick={() => openEditModal(item)}>Edit</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {currentItems.length === 0 && (
                            <TableRow>
                                <TableCell colSpan='10' textAlign='center'>
                                    Empty
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TableHeaderCell colSpan='10'>
                                <div className='twilio-pagination'>
                                    <span>
                                        Showing {sortedData.length === 0 ? 0 : pageStartIndex + 1}-{Math.min(pageStartIndex + itemsPerPage, sortedData.length)} of {sortedData.length}
                                    </span>
                                    <div>
                                        <Button onClick={() => changePage(1)} disabled={currentPage === 1}>First</Button>
                                        <Button onClick={() => changePage(currentPage - 1)} disabled={currentPage === 1}>Previous</Button>
                                        <span className='twilio-page-label'>Page {currentPage} of {totalPages}</span>
                                        <Button onClick={() => changePage(currentPage + 1)} disabled={currentPage === totalPages}>Next</Button>
                                        <Button onClick={() => changePage(totalPages)} disabled={currentPage === totalPages}>Last</Button>
                                    </div>
                                </div>
                            </TableHeaderCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </div>

            {saveMessage && (
                <p className="filter-notes">{saveMessage}</p>
            )}

            <Modal open={editModalOpen} onClose={closeEditModal} className='twilio-edit-modal'>
                <Modal.Header>Edit Website Integration</Modal.Header>
                <Modal.Content>
                    <Grid columns='equal'>
                        <Grid.Row>
                            <Grid.Column>
                                <div className='item'>
                                    <span>Integration ID</span>
                                    <Input value={editedData.integration_id || ''} readOnly />
                                </div>
                            </Grid.Column>
                            <Grid.Column>
                                <div className='item'>
                                    <span>Responsible</span>
                                    <Dropdown
                                        placeholder='select responsible'
                                        fluid
                                        search
                                        selection
                                        options={transformResponsible}
                                        value={editedData.responsible}
                                        onChange={(e, dropdownData) => updateEditedData('responsible', dropdownData.value)}
                                    />
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <div className='item'>
                                    <span>URL / Name</span>
                                    <Input
                                        value={editedData.name || ''}
                                        onChange={(event) => updateEditedData('name', event.target.value)}
                                    />
                                </div>
                            </Grid.Column>
                            <Grid.Column>
                                <div className='item'>
                                    <span>Duplicates Filter</span>
                                    <Input
                                        value={editedData.duplicates_filter || ''}
                                        onChange={(event) => updateEditedData('duplicates_filter', event.target.value)}
                                    />
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <div className='item'>
                                    <span>Project</span>
                                    <Dropdown
                                        placeholder='Select Project'
                                        fluid
                                        search
                                        selection
                                        options={transformProject}
                                        value={editedData.Ad_Project}
                                        onChange={(e, dropdownData) => updateEditedData('Ad_Project', dropdownData.value)}
                                    />
                                </div>
                            </Grid.Column>
                            <Grid.Column>
                                <div className='item'>
                                    <span>Marker</span>
                                    <Input
                                        value={editedData.marker || ''}
                                        onChange={(event) => updateEditedData('marker', event.target.value)}
                                    />
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <div className='item'>
                                    <span>Source Details</span>
                                    <Input
                                        value={editedData.source_details || ''}
                                        onChange={(event) => updateEditedData('source_details', event.target.value)}
                                    />
                                </div>
                            </Grid.Column>
                            <Grid.Column>
                                <div className='item'>
                                    <span>Tags</span>
                                    <Dropdown
                                        placeholder='Select Tags'
                                        fluid
                                        multiple
                                        search
                                        selection
                                        options={transformTags}
                                        value={editedData.tags || []}
                                        onOpen={fetchTags}
                                        onChange={(e, dropdownData) => updateEditedData('tags', dropdownData.value)}
                                    />
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={closeEditModal}>Cancel</Button>
                    <Button primary onClick={handleSave}>Save</Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default function WebsitesData({ refreshKey }){
    const panes = [
        { menuItem: 'Website Integrations', render: () => <TabPane><IntegrationTable refreshKey={refreshKey} /></TabPane> },
    ];

    return(
        <>
            <div className="websites-dataTable">
                <Grid columns={1}>
                    <GridColumn>
                        <Tab panes={panes}/>
                    </GridColumn>
                </Grid>
            </div>
        </>
    );
}
